import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function ClientsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("first_name"); // "first_name" | "last_name"
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/clients"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ first_name: "", last_name: "", email: "", phone: "", notes: "" }); setOpen(true); };

  const save = async () => {
    try {
      const body = { first_name: (editing.first_name || "").trim(), last_name: (editing.last_name || "").trim(), email: editing.email || "", phone: editing.phone || "", notes: editing.notes || "" };
      if (!body.first_name) { toast.error("Il nome è obbligatorio"); return; }
      if (editing.id) await api.patch(`/clients/${editing.id}`, body);
      else await api.post("/clients", body);
      toast.success("Cliente salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare ${c.name}?`)) return;
    try { await api.delete(`/clients/${c.id}`); load(); toast.success("Eliminato"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((c) => {
    const s = q.toLowerCase();
    return !s || c.name.toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s) || (c.phone || "").includes(s);
  }).sort((a, b) => {
    const av = (a[sortBy] || "").toLowerCase();
    const bv = (b[sortBy] || "").toLowerCase();
    return av.localeCompare(bv, "it");
  });

  const canDelete = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Clienti</h1>
          <p className="text-muted-foreground text-sm mt-1">Anagrafica clienti collegabili agli appuntamenti.</p>
        </div>
        <Button onClick={() => { setEditing({ first_name: "", last_name: "", email: "", phone: "", notes: "" }); setOpen(true); }} data-testid="add-client-button">
          <Plus className="h-4 w-4 mr-2" />Nuovo Cliente
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 max-w-md flex-1">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cerca per nome, email o telefono…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="client-search-input" />
        </div>
        <div className="flex items-center gap-1 bg-slate-100 border rounded-md p-0.5" data-testid="clients-sort-toggle">
          <span className="px-2 text-[11px] uppercase font-bold tracking-widest text-muted-foreground">Ordina</span>
          <button onClick={() => setSortBy("first_name")} data-testid="clients-sort-first-name"
            className={`text-xs font-semibold px-3 h-8 rounded transition-colors ${sortBy === "first_name" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            Nome
          </button>
          <button onClick={() => setSortBy("last_name")} data-testid="clients-sort-last-name"
            className={`text-xs font-semibold px-3 h-8 rounded transition-colors ${sortBy === "last_name" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            Cognome
          </button>
        </div>
      </div>

      <div className="bg-card border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Cognome</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Telefono</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                <TableCell className="font-medium">{c.first_name}</TableCell>
                <TableCell className="font-medium">{c.last_name}</TableCell>
                <TableCell className="text-muted-foreground">{c.email}</TableCell>
                <TableCell className="font-mono text-xs">{c.phone}</TableCell>
                <TableCell className="text-muted-foreground text-sm truncate max-w-xs">{c.notes}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => { setEditing({ ...c }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  {canDelete && <Button variant="ghost" size="icon" onClick={() => remove(c)} className="text-rose-600"><Trash2 className="h-4 w-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Modifica" : "Nuovo"} Cliente</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nome</Label><Input value={editing.first_name || ""} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} data-testid="client-first-name-input" /></div>
                <div><Label>Cognome</Label><Input value={editing.last_name || ""} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} data-testid="client-last-name-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              </div>
              <div><Label>Note</Label><Textarea rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-client-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
